import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Calendar, Clock, MapPin, Briefcase, Users } from "lucide-react";
import { cn } from "@/lib/utils";

interface EventCardProps {
  event: any;
  onClick: () => void;
  showDate?: boolean;
}

const eventTypeColors = {
  inspection: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  estimate: "bg-green-500/10 text-green-600 border-green-500/20",
  meeting: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  installation: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  personal: "bg-gray-500/10 text-gray-600 border-gray-500/20"
};

export function EventCard({ event, onClick, showDate }: EventCardProps) {
  const startTime = new Date(event.start_time);
  const endTime = new Date(event.end_time);
  
  return (
    <Card 
      className="p-4 hover:bg-accent/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-semibold text-foreground flex-1">{event.title}</h4>
          <Badge 
            variant="outline" 
            className={cn("capitalize", eventTypeColors[event.event_type as keyof typeof eventTypeColors])}
          >
            {event.event_type}
          </Badge>
        </div>

        <div className="space-y-2 text-sm text-muted-foreground">
          {showDate && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{format(startTime, 'EEEE, MMMM d, yyyy')}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>
              {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
            </span>
          </div>

          {event.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{event.location}</span>
            </div>
          )}

          {event.jobs && (
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              <span>Job #{event.jobs.job_number} - {event.jobs.address}</span>
            </div>
          )}

          {event.leads && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>Lead: {event.leads.company_name}</span>
            </div>
          )}
        </div>

        {event.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {event.description}
          </p>
        )}

        {event.google_event_id && (
          <Badge variant="outline" className="text-xs">
            Synced with Google Calendar
          </Badge>
        )}
      </div>
    </Card>
  );
}